import { PUBLIC_TOOL_SEO } from '../data/publicToolSeoPages';

export const INTERIOR_SHOPPING_TOOL_TYPES = [
  'interior_room_design',
  'full_decoration_change',
  'light_renovation',
  'furniture_change',
  'interior_3d_model_to_realistic',
];

const DECORATION_TO_TOOL_TYPE = {
  'Full Decoration Redesign': 'full_decoration_change',
  'Balanced Redesign': 'light_renovation',
  'Light Refresh': 'furniture_change',
};

/**
 * @param {Record<string, unknown> | null | undefined} project
 * @returns {string}
 */
export function inferProjectToolType(project) {
  if (!project) return 'unknown';

  const designTool = project.designTool;
  if (designTool === 'building-exterior') {
    return 'building_exterior_design';
  }
  if (designTool === 'garden-design') {
    return 'garden_design';
  }
  if (designTool === 'interior-3d-model') {
    return 'interior_3d_model_to_realistic';
  }

  const toolType = project.toolType;
  if (typeof toolType === 'string' && toolType.trim()) {
    return toolType.trim();
  }

  if (project.floorPlanOutputType != null || project.outputType != null) {
    if (project.roomLabel != null || project.selectedRoomLabel != null) {
      return 'floorplan_to_room_image';
    }
    return 'floorplan_2d_to_3d';
  }

  const decoration = project.decorationChangeLevel;
  if (typeof decoration === 'string' && DECORATION_TO_TOOL_TYPE[decoration]) {
    return DECORATION_TO_TOOL_TYPE[decoration];
  }

  if (project.roomType && project.generatedImageUrl) {
    return 'interior_room_design';
  }

  return 'unknown';
}

/**
 * @param {Record<string, unknown> | null | undefined} project
 * @returns {string}
 */
export function getProjectToolType(project) {
  if (!project) return 'unknown';

  const explicit =
    project.toolType ||
    project.designToolType ||
    project.sourceTool ||
    project.aiTool;

  if (typeof explicit === 'string' && explicit.trim()) {
    return explicit.trim();
  }

  return inferProjectToolType(project);
}

/**
 * @param {Record<string, unknown> | null | undefined} project
 * @returns {boolean}
 */
export function isInteriorShoppingEnabled(project) {
  const toolType = getProjectToolType(project);
  return INTERIOR_SHOPPING_TOOL_TYPES.includes(toolType);
}

/**
 * Maps decoration change level to toolType for new interior projects.
 * @param {string} decorationChangeLevel
 * @returns {string}
 */
export function toolTypeFromDecorationLevel(decorationChangeLevel) {
  if (typeof decorationChangeLevel === 'string' && DECORATION_TO_TOOL_TYPE[decorationChangeLevel]) {
    return DECORATION_TO_TOOL_TYPE[decorationChangeLevel];
  }
  return 'interior_room_design';
}

/**
 * Public AI-tool start routes (fresh upload/input page, no edit param).
 * @type {Record<string, string>}
 */
export const TOOL_START_ROUTES = {
  roomDecoration: `${PUBLIC_TOOL_SEO.roomDecoration.path}?decoration=${encodeURIComponent('Full Decoration Redesign')}`,
  furnitureReplacement: `${PUBLIC_TOOL_SEO.furnitureReplacement.path}?decoration=${encodeURIComponent('Light Refresh')}`,
  lightRenovation: `${PUBLIC_TOOL_SEO.furnitureReplacement.path}?decoration=${encodeURIComponent('Balanced Redesign')}`,
  interior3dModelToRealistic: PUBLIC_TOOL_SEO.interior3dModelToRealistic.path,
  exteriorDesign: PUBLIC_TOOL_SEO.exteriorDesign.path,
  gardenDesign: PUBLIC_TOOL_SEO.gardenDesign.path,
  floorPlanTo3d: PUBLIC_TOOL_SEO.floorPlanTo3d.path,
  floorPlanRoomRender: PUBLIC_TOOL_SEO.floorPlanRoomRender.path,
};

/**
 * @typedef {'new' | 'original' | 'compare'} ResultViewMode
 */

/**
 * @param {string | null | undefined} mode
 * @param {{ canCompare?: boolean; hasOriginal?: boolean }} [options]
 * @returns {ResultViewMode}
 */
export function normalizeResultViewMode(mode, { canCompare = true, hasOriginal = true } = {}) {
  if (mode === 'original') {
    return hasOriginal ? 'original' : 'new';
  }
  if (mode === 'compare') {
    return canCompare ? 'compare' : 'new';
  }
  return 'new';
}

/**
 * @param {string} resultViewMode - 'new' | 'original' | 'compare' | 'split' (legacy)
 * @returns {'redesigned' | 'original' | 'compare'}
 */
export function resultViewModeToViewMode(resultViewMode) {
  switch (resultViewMode) {
    case 'original':
      return 'original';
    case 'compare':
      return 'compare';
    case 'split':
    case 'new':
    default:
      return 'redesigned';
  }
}

/**
 * @param {'redesigned' | 'original' | 'compare' | 'both'} viewMode
 * @returns {ResultViewMode}
 */
export function viewModeToResultViewMode(viewMode) {
  switch (viewMode) {
    case 'original':
      return 'original';
    case 'compare':
      return 'compare';
    case 'both':
    case 'redesigned':
    default:
      return 'new';
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} project
 * @returns {string}
 */
export function getProjectToolLabel(project) {
  const toolType = getProjectToolType(project);

  switch (toolType) {
    case 'full_decoration_change':
      return 'Full decoration change';
    case 'light_renovation':
      return 'Room furniture replacement';
    case 'furniture_change':
      return 'Room furniture replacement';
    case 'building_exterior_design':
      return 'Exterior renovation';
    case 'garden_design':
      return 'Garden design';
    case 'floorplan_2d_to_3d':
      return '2D plan to 3D view';
    case 'floorplan_to_room_image':
      return '2D floor plan to room image';
    case 'interior_room_design':
      return 'Room redesign';
    case 'interior_3d_model_to_realistic':
      return '3D model to realistic interior';
    default: {
      const decoration = project?.decorationChangeLevel;
      if (decoration === 'Full Decoration Redesign') return 'Full decoration change';
      if (decoration === 'Balanced Redesign') return 'Room furniture replacement';
      if (decoration === 'Light Refresh') return 'Room furniture replacement';
      return 'AI design';
    }
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} project
 * @returns {string | null}
 */
export function getProjectEditRoute(project) {
  if (!project?.id) return null;
  const id = encodeURIComponent(String(project.id));
  const toolType = getProjectToolType(project);

  switch (toolType) {
    case 'building_exterior_design':
      return `/projects/ai-exterior-design?edit=${id}`;
    case 'garden_design':
      return `/projects/ai-garden-design?edit=${id}`;
    case 'floorplan_2d_to_3d':
      return `/projects/ai-2d-floorplan-to-3d?edit=${id}`;
    case 'floorplan_to_room_image':
      return `/projects/ai-2d-floorplan-to-room-render?edit=${id}`;
    case 'full_decoration_change':
      return `/projects/ai-room-decoration?decoration=${encodeURIComponent('Full Decoration Redesign')}&edit=${id}`;
    case 'furniture_change':
      return `/projects/ai-furniture-replacement?decoration=${encodeURIComponent('Light Refresh')}&edit=${id}`;
    case 'light_renovation':
      return `/projects/ai-furniture-replacement?decoration=${encodeURIComponent('Balanced Redesign')}&edit=${id}`;
    case 'interior_3d_model_to_realistic':
      return `/ai-tools/ai-3d-model-to-realistic-interior?edit=${id}`;
    case 'interior_room_design':
    default:
      return `/projects/ai-furniture-replacement?edit=${id}`;
  }
}

/**
 * Fresh start route for the project's AI tool (public /ai-tools path, no edit param).
 * @param {Record<string, unknown> | null | undefined} project
 * @returns {string}
 */
export function getProjectToolStartRoute(project) {
  const toolType = getProjectToolType(project);

  switch (toolType) {
    case 'full_decoration_change':
      return TOOL_START_ROUTES.roomDecoration;
    case 'furniture_change':
      return TOOL_START_ROUTES.furnitureReplacement;
    case 'light_renovation':
      return TOOL_START_ROUTES.lightRenovation;
    case 'interior_3d_model_to_realistic':
      return TOOL_START_ROUTES.interior3dModelToRealistic;
    case 'building_exterior_design':
      return TOOL_START_ROUTES.exteriorDesign;
    case 'garden_design':
      return TOOL_START_ROUTES.gardenDesign;
    case 'floorplan_2d_to_3d':
      return TOOL_START_ROUTES.floorPlanTo3d;
    case 'floorplan_to_room_image':
      return TOOL_START_ROUTES.floorPlanRoomRender;
    case 'interior_room_design':
    default:
      return TOOL_START_ROUTES.furnitureReplacement;
  }
}
